import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, ShieldOff, Gift, Plus, Layers, Search, Copy, Check, RefreshCw, User, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { fmtDate } from '@/lib/utils';
import { useToast } from '@/components/ui/use-toast';
import { useBookingExperiences } from '@/lib/useBookingExperiences';
import GiftCardEditModal from '@/components/admin/GiftCardEditModal';
import { useAuth } from '@/lib/AuthContext';

const inputCls = "bg-[#111115] border-white/10 text-white h-11 placeholder:text-white/20 focus:border-primary/60";
const labelCls = "text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block";

const PRESETS = [25, 50, 100, 150, 250];

export default function AdminGiftCards() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user: currentUser, isLoadingAuth: authLoading } = useAuth();
  const [tab, setTab] = useState('list');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);

  // Create single
  const [single, setSingle] = useState({ amount: 50, experienceType: '', recipientName: '', recipientEmail: '', deliveryMethod: 'email', recipientAddressLine1: '', recipientAddressLine2: '', recipientCity: '', recipientPostcode: '', giftMessage: '', expiryDate: '', notes: '' });
  const [selectedCustomerId, setSelectedCustomerId] = useState('manual');
  // Bulk
  const [bulk, setBulk] = useState({ amount: 50, count: 10 });
  const experiences = useBookingExperiences();

  const [created, setCreated] = useState([]);
  const [editingCard, setEditingCard] = useState(null);

  const { data: cards = [], isLoading } = useQuery({
    queryKey: ['admin-gift-cards'],
    queryFn: () => base44.entities.GiftCard.list('-created_date', 1000),
    enabled: currentUser?.role === 'admin',
  });

  const { data: redemptions = [] } = useQuery({
    queryKey: ['admin-gift-redemptions'],
    queryFn: () => base44.entities.GiftCardRedemption.list('-created_date', 1000),
    enabled: currentUser?.role === 'admin',
  });

  // Existing customers for the recipient dropdown
  const { data: users = [] } = useQuery({
    queryKey: ['admin-gift-users'],
    queryFn: () => base44.entities.User.list('-created_date', 500),
    enabled: currentUser?.role === 'admin' && tab === 'single',
  });

  const customerOptions = users
    .filter(u => u.email)
    .map(u => ({ id: u.id, email: u.email, name: u.full_name || '' }))
    .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email));

  const handleSelectCustomer = (value) => {
    setSelectedCustomerId(value);
    if (value === 'manual') return;
    const u = users.find(x => x.id === value);
    if (u) {
      setSingle(s => ({ ...s, recipientName: u.full_name || '', recipientEmail: u.email || '' }));
    }
  };

  if (authLoading) {
    return <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }
  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
        </div>
      </div>
    );
  }

  const filtered = cards.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.code?.toLowerCase().includes(q)) || (c.purchaser_email?.toLowerCase().includes(q)) || (c.recipient_name?.toLowerCase().includes(q)) || (c.recipient_email?.toLowerCase().includes(q));
  });

  const totalActiveBalance = cards.filter(c => c.status === 'active').reduce((s, c) => s + (c.balance || 0), 0);
  const totalIssued = cards.reduce((s, c) => s + (c.initial_amount || 0), 0);
  const totalRedeemed = redemptions.reduce((s, r) => s + (r.amount || 0), 0);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-gift-cards'] });
    queryClient.invalidateQueries({ queryKey: ['admin-gift-redemptions'] });
  };

  const handleCreateSingle = async (e) => {
    e.preventDefault();
    if (!single.amount || single.amount <= 0) { toast({ variant: 'destructive', title: 'Enter a valid amount' }); return; }
    setCreating(true);
    setCreated([]);
    try {
      const res = await base44.functions.invoke('adminCreateGiftCards', {
        mode: 'single',
        amount: Number(single.amount),
        recipientName: single.recipientName,
        recipientEmail: single.recipientEmail,
        deliveryMethod: single.deliveryMethod,
        recipientAddressLine1: single.recipientAddressLine1,
        recipientAddressLine2: single.recipientAddressLine2,
        recipientCity: single.recipientCity,
        recipientPostcode: single.recipientPostcode,
        giftMessage: single.giftMessage,
        expiryDate: single.expiryDate,
        experienceType: single.experienceType,
        notes: single.notes,
      });
      if (res.data?.success) {
        setCreated(res.data.created);
        toast({ title: `Created ${res.data.count} gift card(s)` });
        refresh();
      } else {
        toast({ variant: 'destructive', title: res.data?.error || 'Failed' });
      }
    } catch (err) {
      toast({ variant: 'destructive', title: 'Failed to create gift card' });
    }
    setCreating(false);
  };

  const handleCreateBulk = async (e) => {
    e.preventDefault();
    if (!bulk.amount || bulk.amount <= 0) { toast({ variant: 'destructive', title: 'Enter a valid amount' }); return; }
    if (!bulk.count || bulk.count < 1) { toast({ variant: 'destructive', title: 'Enter a count' }); return; }
    setCreating(true);
    setCreated([]);
    try {
      const res = await base44.functions.invoke('adminCreateGiftCards', {
        mode: 'bulk',
        amount: Number(bulk.amount),
        count: Number(bulk.count),
      });
      if (res.data?.success) {
        setCreated(res.data.created);
        toast({ title: `Generated ${res.data.count} gift card(s)` });
        refresh();
      } else {
        toast({ variant: 'destructive', title: res.data?.error || 'Failed' });
      }
    } catch (err) {
      toast({ variant: 'destructive', title: 'Failed to generate gift cards' });
    }
    setCreating(false);
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code);
    toast({ title: 'Copied ' + code });
  };

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      {/* Header */}
      <section className="py-14 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-wider mb-4 transition-colors">
              <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
            </Link>
          </div>
          <p className="text-primary text-xs font-bold tracking-widest mb-2">PRODUCTS &amp; CATALOG</p>
          <h1 className="text-white font-black text-3xl md:text-4xl uppercase">Gift Card Management</h1>
          <p className="text-white/30 text-sm mt-1">Create gift cards, generate bulk codes, and track redemptions.</p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 max-w-2xl">
            <div className="bg-[#111115] border border-white/5 p-4">
              <p className="text-white/30 text-[10px] font-bold tracking-widest">ISSUED</p>
              <p className="text-white font-black text-xl mt-1">£{totalIssued.toFixed(2)}</p>
            </div>
            <div className="bg-[#111115] border border-white/5 p-4">
              <p className="text-white/30 text-[10px] font-bold tracking-widest">OUTSTANDING</p>
              <p className="text-primary font-black text-xl mt-1">£{totalActiveBalance.toFixed(2)}</p>
            </div>
            <div className="bg-[#111115] border border-white/5 p-4">
              <p className="text-white/30 text-[10px] font-bold tracking-widest">REDEEMED</p>
              <p className="text-white font-black text-xl mt-1">£{totalRedeemed.toFixed(2)}</p>
            </div>
            <div className="bg-[#111115] border border-white/5 p-4">
              <p className="text-white/30 text-[10px] font-bold tracking-widest">CARDS</p>
              <p className="text-white font-black text-xl mt-1">{cards.length}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <div className="border-b border-white/5 bg-[#0d0d0f] sticky top-16 z-30">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 flex items-center gap-0 overflow-x-auto">
          {[
            { key: 'list', label: 'ALL GIFT CARDS', icon: Gift },
            { key: 'single', label: 'CREATE SINGLE', icon: Plus },
            { key: 'bulk', label: 'GENERATE BULK', icon: Layers },
            { key: 'redemptions', label: 'REDEMPTIONS', icon: RefreshCw },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`shrink-0 text-xs font-bold tracking-wider px-5 py-4 border-b-2 transition-all whitespace-nowrap flex items-center gap-2 ${
                tab === t.key ? 'border-primary text-primary' : 'border-transparent text-white/30 hover:text-white/60'
              }`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      <section className="px-6 lg:px-10 py-10 max-w-6xl mx-auto">
        {/* ALL CARDS */}
        {tab === 'list' && (
          <div>
            <div className="relative mb-6 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search code, email, name..." className="bg-[#111115] border-white/10 text-white pl-9 h-11 placeholder:text-white/20 focus:border-primary/60" />
            </div>
            {isLoading ? (
              <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-20 border border-white/5 bg-[#111115]">
                <Gift className="w-10 h-10 text-white/10 mx-auto mb-4" />
                <p className="text-white/30 text-sm">No gift cards found</p>
              </div>
            ) : (
              <div className="border border-white/5 overflow-x-auto">
                <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-[#111115] border-b border-white/5 min-w-[800px]">
                  <p className="col-span-3 text-white/30 text-[10px] font-bold tracking-widest">CODE</p>
                  <p className="col-span-2 text-white/30 text-[10px] font-bold tracking-widest">BALANCE</p>
                  <p className="col-span-2 text-white/30 text-[10px] font-bold tracking-widest">SOURCE</p>
                  <p className="col-span-2 text-white/30 text-[10px] font-bold tracking-widest">RECIPIENT</p>
                  <p className="col-span-1 text-white/30 text-[10px] font-bold tracking-widest">STATUS</p>
                  <p className="col-span-2 text-white/30 text-[10px] font-bold tracking-widest">CREATED</p>
                </div>
                {filtered.map(c => (
                  <div key={c.id} onClick={() => setEditingCard(c)} className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/5 hover:bg-white/5 transition-colors items-center min-w-[800px] cursor-pointer">
                    <div className="col-span-3">
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <span className="text-primary font-mono font-bold text-sm">{c.code}</span>
                        <button onClick={() => copyCode(c.code)} className="text-white/20 hover:text-white/60 transition-colors"><Copy className="w-3.5 h-3.5" /></button>
                      </div>
                      {c.experience_type && (
                        <p className="text-white/30 text-[10px] mt-0.5 truncate">{(experiences.find(e => e.key === c.experience_type)?.name) || c.experience_type}</p>
                      )}
                    </div>
                    <p className="col-span-2 text-white text-sm font-bold">£{(c.balance || 0).toFixed(2)}<span className="text-white/30 font-normal text-xs"> / £{(c.initial_amount || 0).toFixed(2)}</span></p>
                    <p className="col-span-2">
                      <span className={`inline-block text-[10px] font-bold tracking-wider px-2 py-1 ${c.source === 'admin' ? 'bg-white/5 text-white/50' : 'bg-primary/10 text-primary'}`}>{c.source?.toUpperCase()}</span>
                    </p>
                    <p className="col-span-2 text-white/50 text-xs truncate">{c.recipient_name || c.purchaser_name || '—'}</p>
                    <p className="col-span-1">
                      <span className={`inline-block text-[10px] font-bold tracking-wider px-2 py-1 ${c.status === 'active' ? 'bg-green-500/10 text-green-400' : c.status === 'redeemed' ? 'bg-white/5 text-white/40' : 'bg-red-500/10 text-red-400'}`}>{c.status?.toUpperCase()}</span>
                    </p>
                    <p className="col-span-2 text-white/30 text-xs">{fmtDate(c.created_date) || '—'}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* CREATE SINGLE */}
        {tab === 'single' && (
          <form onSubmit={handleCreateSingle} className="max-w-2xl space-y-6">
            <div className="bg-[#111115] border border-white/5 p-6 space-y-5">
              <div>
                <Label className={labelCls}>AMOUNT (£) *</Label>
                <Input type="number" min={1} max={1000} value={single.amount} onChange={e => setSingle({ ...single, amount: e.target.value })} className={inputCls} />
                <div className="flex gap-2 mt-2">
                  {PRESETS.map(v => (
                    <button key={v} type="button" onClick={() => setSingle({ ...single, amount: v, experienceType: '' })} className="px-3 py-1.5 text-xs font-bold bg-white/5 border border-white/10 text-white/60 hover:border-primary/40 hover:text-white">£{v}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label className={labelCls}>OR CREATE AN EXPERIENCE VOUCHER</Label>
                <div className="space-y-2">
                  <button type="button" onClick={() => setSingle({ ...single, experienceType: '' })}
                    className={`w-full text-left px-3 py-2.5 text-xs font-bold tracking-wider border transition-all ${!single.experienceType ? 'bg-primary/10 border-primary text-primary' : 'bg-white/5 border-white/10 text-white/50 hover:border-primary/40'}`}>
                    NONE (MONETARY)
                  </button>
                  {experiences.map(exp => (
                    <button key={exp.key} type="button"
                      onClick={() => setSingle({ ...single, experienceType: exp.key, amount: exp.price })}
                      className={`w-full flex items-center justify-between px-3 py-2.5 text-xs font-bold border transition-all ${single.experienceType === exp.key ? 'bg-primary/10 border-primary text-primary' : 'bg-white/5 border-white/10 text-white/60 hover:border-primary/40'}`}>
                      <span>{exp.name}{exp.tag ? ` · ${exp.tag}` : ''}</span>
                      <span className="font-black">£{Number(exp.price).toFixed(2)}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className={labelCls}>SELECT CUSTOMER</Label>
                <Select value={selectedCustomerId} onValueChange={handleSelectCustomer}>
                  <SelectTrigger className="bg-[#111115] border-white/10 text-white h-11 focus:border-primary/60">
                    <SelectValue placeholder="Choose an existing customer..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111115] border-white/10 text-white max-h-64">
                    <SelectItem value="manual">✎ Enter email manually (one-off / new customer)</SelectItem>
                    {customerOptions.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name ? `${u.name} — ${u.email}` : u.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedCustomerId !== 'manual' && (
                  <p className="text-white/30 text-[10px] mt-1.5">Customer details auto-filled below — edit if needed.</p>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className={labelCls}>RECIPIENT NAME</Label>
                  <Input value={single.recipientName} onChange={e => { setSingle({ ...single, recipientName: e.target.value }); setSelectedCustomerId('manual'); }} className={inputCls} placeholder="Optional" />
                </div>
                <div>
                  <Label className={labelCls}>RECIPIENT EMAIL</Label>
                  <Input type="email" value={single.recipientEmail} onChange={e => { setSingle({ ...single, recipientEmail: e.target.value }); setSelectedCustomerId('manual'); }} className={inputCls} placeholder="Optional" />
                </div>
              </div>
              <div>
                <Label className={labelCls}>DELIVERY METHOD</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button type="button" onClick={() => setSingle({ ...single, deliveryMethod: 'email' })} className={`py-3 text-xs font-bold tracking-wider ${single.deliveryMethod === 'email' ? 'bg-primary text-white' : 'bg-white/5 border border-white/10 text-white/60'}`}>EMAIL</button>
                  <button type="button" onClick={() => setSingle({ ...single, deliveryMethod: 'post' })} className={`py-3 text-xs font-bold tracking-wider ${single.deliveryMethod === 'post' ? 'bg-primary text-white' : 'bg-white/5 border border-white/10 text-white/60'}`}>POST</button>
                </div>
              </div>
              {single.deliveryMethod === 'post' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2"><Label className={labelCls}>ADDRESS LINE 1</Label><Input value={single.recipientAddressLine1} onChange={e => setSingle({ ...single, recipientAddressLine1: e.target.value })} className={inputCls} /></div>
                  <div className="sm:col-span-2"><Label className={labelCls}>ADDRESS LINE 2</Label><Input value={single.recipientAddressLine2} onChange={e => setSingle({ ...single, recipientAddressLine2: e.target.value })} className={inputCls} /></div>
                  <div><Label className={labelCls}>CITY</Label><Input value={single.recipientCity} onChange={e => setSingle({ ...single, recipientCity: e.target.value })} className={inputCls} /></div>
                  <div><Label className={labelCls}>POSTCODE</Label><Input value={single.recipientPostcode} onChange={e => setSingle({ ...single, recipientPostcode: e.target.value })} className={inputCls} /></div>
                </div>
              )}
              <div>
                <Label className={labelCls}>GIFT MESSAGE</Label>
                <textarea value={single.giftMessage} onChange={e => setSingle({ ...single, giftMessage: e.target.value })} rows={2} className="w-full bg-[#111115] border border-white/10 text-white text-sm px-3 py-2.5 focus:border-primary/60 outline-none resize-none" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label className={labelCls}>EXPIRY DATE (OPTIONAL)</Label><Input type="date" value={single.expiryDate} onChange={e => setSingle({ ...single, expiryDate: e.target.value })} className={inputCls} /></div>
                <div><Label className={labelCls}>INTERNAL NOTES</Label><Input value={single.notes} onChange={e => setSingle({ ...single, notes: e.target.value })} className={inputCls} /></div>
              </div>
            </div>
            <button type="submit" disabled={creating} className="w-full bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest py-4 disabled:opacity-40 flex items-center justify-center gap-2">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} CREATE GIFT CARD
            </button>
            {created.length > 0 && (
              <div className="bg-green-500/5 border border-green-500/20 p-4">
                <p className="text-green-400 text-xs font-bold tracking-widest mb-2">CREATED</p>
                {created.map(c => (
                  <div key={c.id} className="flex items-center justify-between py-1">
                    <span className="text-primary font-mono font-bold">{c.code}</span>
                    <span className="text-white/50 text-xs">£{c.balance.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </form>
        )}

        {/* BULK */}
        {tab === 'bulk' && (
          <form onSubmit={handleCreateBulk} className="max-w-md space-y-6">
            <div className="bg-[#111115] border border-white/5 p-6 space-y-5">
              <div>
                <Label className={labelCls}>AMOUNT PER CARD (£) *</Label>
                <Input type="number" min={1} max={1000} value={bulk.amount} onChange={e => setBulk({ ...bulk, amount: e.target.value })} className={inputCls} />
              </div>
              <div>
                <Label className={labelCls}>NUMBER OF CODES * (max 200)</Label>
                <Input type="number" min={1} max={200} value={bulk.count} onChange={e => setBulk({ ...bulk, count: e.target.value })} className={inputCls} />
              </div>
              <p className="text-white/30 text-xs">Generates <span className="text-primary font-bold">{bulk.count || 0}</span> unique gift cards of <span className="text-primary font-bold">£{Number(bulk.amount || 0).toFixed(2)}</span> each.</p>
            </div>
            <button type="submit" disabled={creating} className="w-full bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest py-4 disabled:opacity-40 flex items-center justify-center gap-2">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Layers className="w-4 h-4" />} GENERATE CODES
            </button>
            {created.length > 0 && (
              <div className="bg-green-500/5 border border-green-500/20 p-4">
                <p className="text-green-400 text-xs font-bold tracking-widest mb-2">{created.length} CODES GENERATED</p>
                <div className="max-h-64 overflow-y-auto space-y-1">
                  {created.map(c => (
                    <div key={c.id} className="flex items-center justify-between py-1">
                      <span className="text-primary font-mono font-bold text-sm">{c.code}</span>
                      <button onClick={() => copyCode(c.code)} className="text-white/20 hover:text-white/60"><Copy className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </form>
        )}

        {/* REDEMPTIONS */}
        {tab === 'redemptions' && (
          <div>
            {redemptions.length === 0 ? (
              <div className="text-center py-20 border border-white/5 bg-[#111115]">
                <RefreshCw className="w-10 h-10 text-white/10 mx-auto mb-4" />
                <p className="text-white/30 text-sm">No redemptions yet</p>
              </div>
            ) : (
              <div className="border border-white/5 overflow-x-auto">
                <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-[#111115] border-b border-white/5 min-w-[640px]">
                  <p className="col-span-3 text-white/30 text-[10px] font-bold tracking-widest">CODE</p>
                  <p className="col-span-2 text-white/30 text-[10px] font-bold tracking-widest">AMOUNT</p>
                  <p className="col-span-3 text-white/30 text-[10px] font-bold tracking-widest">CUSTOMER</p>
                  <p className="col-span-2 text-white/30 text-[10px] font-bold tracking-widest">ORDER IDs</p>
                  <p className="col-span-2 text-white/30 text-[10px] font-bold tracking-widest">DATE</p>
                </div>
                {redemptions.map(r => (
                  <div key={r.id} className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/5 items-center min-w-[640px]">
                    <span className="col-span-3 text-primary font-mono font-bold text-sm">{r.gift_card_code}</span>
                    <p className="col-span-2 text-white font-bold">£{(r.amount || 0).toFixed(2)}</p>
                    <p className="col-span-3 text-white/50 text-xs truncate">{r.customer_email || '—'}</p>
                    <p className="col-span-2 text-white/40 text-xs truncate">{r.order_ids || r.order_id || '—'}</p>
                    <p className="col-span-2 text-white/30 text-xs">{fmtDate(r.created_date) || '—'}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {editingCard && (
        <GiftCardEditModal
          card={editingCard}
          onClose={() => setEditingCard(null)}
          onSaved={(updated) => {
            setEditingCard(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}